import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Button,
    Descriptions,
    Modal,
    Radio,
    Select,
    Skeleton,
    Space,
    Tag,
    Typography,
} from 'antd';
import { useAuth } from '../../../hooks/useAuth';
import {
    aiAdminApi,
    ModelBeliefsAdhocTarget,
    ModelBeliefsTriggerOverrides,
    ModelBeliefsTriggerResponse,
} from '../../../services/ai-admin-api';

const { Text, Paragraph } = Typography;

export type ModelBeliefsRunTarget =
    | { kind: 'brand'; brandId: number; name: string }
    | { kind: 'adhoc'; target: ModelBeliefsAdhocTarget; templateType: string; configId?: number };

interface Props {
    open: boolean;
    target: ModelBeliefsRunTarget | null;
    onClose: () => void;
    // Called with the queued run's id once the read has started.
    onStarted: (runId: number) => void;
}

// Estimate first, then start. A read costs real money, so the plan is put in
// front of the person before the button that spends: calls by model, targets,
// what is searched and what is read from the pages. The estimate is the dry
// run, which plans the run without creating it.
export const ModelBeliefsRunModal: React.FC<Props> = ({ open, target, onClose, onStarted }) => {
    const { user } = useAuth();
    const jwt = user?.jwtToken ?? '';

    const [source, setSource] = useState<'brand' | 'admin'>('brand');
    const [markets, setMarkets] = useState<{ country: string; surveys: number }[]>([]);
    const [country, setCountry] = useState<string | undefined>(undefined);
    const [loadingBrand, setLoadingBrand] = useState(false);
    const [estimate, setEstimate] = useState<ModelBeliefsTriggerResponse | null>(null);
    const [estimating, setEstimating] = useState(false);
    const [starting, setStarting] = useState(false);
    const [started, setStarted] = useState<ModelBeliefsTriggerResponse | null>(null);
    const [error, setError] = useState<string | null>(null);

    // A brand's market is a per-run choice; when it sells in more than one the
    // read has to be told which. Loaded from the config so the modal offers
    // the same list the config form does.
    useEffect(() => {
        if (!open || !target || !jwt) return;
        setSource('brand');
        setEstimate(null);
        setStarted(null);
        setError(null);
        setMarkets([]);
        setCountry(undefined);
        if (target.kind !== 'brand') return;
        let cancelled = false;
        setLoadingBrand(true);
        aiAdminApi
            .getModelBeliefsBrandConfig(jwt, target.brandId)
            .then((response) => {
                if (cancelled) return;
                setMarkets(response.markets ?? []);
                setCountry(
                    response.config?.marketCountry ??
                        (response.markets?.length === 1 ? response.markets[0].country : undefined),
                );
            })
            .catch((e) => !cancelled && setError((e as Error).message))
            .finally(() => !cancelled && setLoadingBrand(false));
        return () => {
            cancelled = true;
        };
    }, [open, target, jwt]);

    const overrides = useCallback(
        (dryRun: boolean): ModelBeliefsTriggerOverrides => {
            if (!target) return { dryRun };
            if (target.kind === 'brand') {
                return { dryRun, triggerSource: source, country };
            }
            return { dryRun, templateType: target.templateType, configId: target.configId };
        },
        [target, source, country],
    );

    const call = useCallback(
        async (dryRun: boolean) => {
            if (!jwt || !target) throw new Error('Nothing to run');
            return target.kind === 'brand'
                ? aiAdminApi.triggerModelBeliefsRun(jwt, target.brandId, overrides(dryRun))
                : aiAdminApi.triggerModelBeliefsAdhocRun(jwt, target.target, overrides(dryRun));
        },
        [jwt, target, overrides],
    );

    const runEstimate = async () => {
        setEstimating(true);
        setError(null);
        try {
            setEstimate(await call(true));
        } catch (e) {
            setError((e as Error).message);
        } finally {
            setEstimating(false);
        }
    };

    const start = async () => {
        setStarting(true);
        setError(null);
        try {
            const response = await call(false);
            setStarted(response);
            if (response.runId) onStarted(response.runId);
        } catch (e) {
            setError((e as Error).message);
        } finally {
            setStarting(false);
        }
    };

    const title =
        target?.kind === 'brand'
            ? `Run a read of ${target.name}`
            : target
              ? `Run a read of ${target.target.name}`
              : 'Run a read';

    const needsMarket = target?.kind === 'brand' && markets.length > 1 && !country;

    return (
        <Modal
            open={open}
            title={title}
            width={760}
            onCancel={onClose}
            destroyOnClose
            footer={
                started ? (
                    <Button type="primary" onClick={onClose}>
                        Close
                    </Button>
                ) : (
                    <Space>
                        <Button onClick={onClose}>Cancel</Button>
                        <Button
                            onClick={() => void runEstimate()}
                            loading={estimating}
                            disabled={loadingBrand || needsMarket || starting}
                        >
                            {estimate ? 'Estimate again' : 'Estimate'}
                        </Button>
                        <Button
                            type="primary"
                            onClick={() => void start()}
                            loading={starting}
                            disabled={!estimate || estimating || needsMarket}
                        >
                            Start the read
                        </Button>
                    </Space>
                )
            }
        >
            <Space direction="vertical" size={14} style={{ width: '100%' }}>
                {target?.kind === 'brand' && (
                    <Space direction="vertical" size={6} style={{ width: '100%' }}>
                        <Text strong style={{ fontSize: 13 }}>
                            Source
                        </Text>
                        <Radio.Group
                            value={source}
                            onChange={(e) => {
                                setSource(e.target.value);
                                setEstimate(null);
                            }}
                        >
                            <Space direction="vertical" size={4}>
                                <Radio value="brand">
                                    <Text>Brand read</Text>{' '}
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        shows on The Machines, counts in the brand&rsquo;s history and
                                        starts its cooldown
                                    </Text>
                                </Radio>
                                <Radio value="admin">
                                    <Text>Admin run</Text>{' '}
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        calibration or a check; Hub only, the customer never sees it
                                    </Text>
                                </Radio>
                            </Space>
                        </Radio.Group>
                    </Space>
                )}

                {target?.kind === 'brand' && loadingBrand && <Skeleton active paragraph={{ rows: 1 }} />}

                {target?.kind === 'brand' && !loadingBrand && markets.length > 1 && (
                    <Space direction="vertical" size={6} style={{ width: '100%' }}>
                        <Text strong style={{ fontSize: 13 }}>
                            Market
                        </Text>
                        <Select
                            style={{ width: 260 }}
                            value={country}
                            placeholder="Which market is this read for?"
                            onChange={(value: string) => {
                                setCountry(value);
                                setEstimate(null);
                            }}
                            options={markets.map((m) => ({
                                value: m.country,
                                label: `${m.country} · ${m.surveys} survey${m.surveys === 1 ? '' : 's'}`,
                            }))}
                        />
                        <Text type="secondary" style={{ fontSize: 11 }}>
                            The brand sells in more than one market and each has its own beliefs, so the
                            read has to be told which one it is for.
                        </Text>
                    </Space>
                )}

                {target?.kind === 'adhoc' && (
                    <Descriptions size="small" column={2} bordered>
                        <Descriptions.Item label="Brand">{target.target.name}</Descriptions.Item>
                        <Descriptions.Item label="Website">{target.target.websiteUrl}</Descriptions.Item>
                        <Descriptions.Item label="Category">{target.target.category}</Descriptions.Item>
                        <Descriptions.Item label="Market">
                            {target.target.country}
                            {target.target.city ? ` · ${target.target.city}` : ''}
                        </Descriptions.Item>
                        <Descriptions.Item label="Template">{target.templateType}</Descriptions.Item>
                        <Descriptions.Item label="Competitors">
                            {target.target.competitorNames.length > 0
                                ? target.target.competitorNames.join(', ')
                                : 'none'}
                        </Descriptions.Item>
                    </Descriptions>
                )}

                {error && <Alert type="error" showIcon message={error} />}

                {!estimate && !started && (
                    <Paragraph type="secondary" style={{ margin: 0, fontSize: 12 }}>
                        Estimate plans the read without spending anything: how many calls, on which models,
                        for which targets. The read can only be started once it has been estimated.
                    </Paragraph>
                )}

                {estimate && !started && (
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                        <Descriptions size="small" column={2} bordered>
                            <Descriptions.Item label="Targets" span={2}>
                                {(estimate.targets ?? []).join(', ')}
                                {estimate.competitorsDropped && estimate.competitorsDropped.length > 0 && (
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        {' '}
                                        (not read: {estimate.competitorsDropped.join(', ')})
                                    </Text>
                                )}
                            </Descriptions.Item>
                            <Descriptions.Item label="Calls">
                                {estimate.callsBudget}
                                {estimate.maxCallsWithTopUp && estimate.maxCallsWithTopUp > estimate.callsBudget
                                    ? ` (up to ${estimate.maxCallsWithTopUp} with top-up)`
                                    : ''}
                            </Descriptions.Item>
                            <Descriptions.Item label="Of which">
                                {estimate.statementCalls ?? 0} statements · {estimate.gateCalls ?? 0} rank question
                            </Descriptions.Item>
                            <Descriptions.Item label="By model" span={2}>
                                <Space size={4} wrap>
                                    {Object.entries(estimate.callsByModel ?? {}).map(([label, calls]) => (
                                        <Tag key={label}>
                                            {label} · {calls}
                                        </Tag>
                                    ))}
                                </Space>
                            </Descriptions.Item>
                            <Descriptions.Item label="Statements">
                                {estimate.statements ?? 0} asked
                                {estimate.statementsNotAsked
                                    ? `, ${estimate.statementsNotAsked} left to the survey`
                                    : ''}
                            </Descriptions.Item>
                            <Descriptions.Item label="Market">{estimate.country ?? country ?? '-'}</Descriptions.Item>
                            <Descriptions.Item label="Searched statements">
                                {(estimate.settings as any)?.searchedStatements === false ? (
                                    <Tag>off: read from the pages</Tag>
                                ) : (
                                    <Tag color="blue">on</Tag>
                                )}
                            </Descriptions.Item>
                            <Descriptions.Item label="Page read">
                                {estimate.pageRead?.enabled ? (
                                    <span>
                                        <Tag color="blue">on</Tag>
                                        {estimate.pageRead.reader?.label ?? estimate.pageRead.reader?.model ?? ''}
                                        {estimate.pageRead.minAnswers
                                            ? `, pages read in ${estimate.pageRead.minAnswers}+ answers`
                                            : ''}
                                    </span>
                                ) : (
                                    <Tag>off</Tag>
                                )}
                            </Descriptions.Item>
                        </Descriptions>
                        {estimate.budgetScaled && (
                            <Alert
                                type="warning"
                                showIcon
                                message={`Scaled to fit the call budget (factor ${estimate.scaleFactor.toFixed(2)})`}
                            />
                        )}
                        <Text type="secondary" style={{ fontSize: 11 }}>
                            The page read is priced after the rank question has run, since how many pages
                            qualify is only known then: roughly six calls per page on the reader model.
                        </Text>
                    </Space>
                )}

                {started && (
                    <Alert
                        type="success"
                        showIcon
                        message={`Run #${started.runId} queued`}
                        description={`It starts within ${started.startsWithin ?? 'a minute'}. Watch it on the Runs tab; the read, the pages and the written synthesis appear there when it is READY.`}
                    />
                )}
            </Space>
        </Modal>
    );
};
